import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Table, 
  Card, 
  Badge, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Pagination,
  Button,
  Modal,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { edgeDeviceAPI, clearCache } from '../utils/api';
import type { EdgeDevice, CreateEdgeDeviceRequest } from '../types/edgedevice';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';

// Lightweight icon replacements to reduce bundle size
const EdgeDeviceIcon = () => (
  <i className="bi bi-cpu me-1"></i>
);




// Memoized EdgeDevice Card Component for Mobile
const EdgeDeviceCard = memo(({ edgeDevice }: {
  edgeDevice: EdgeDevice;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'edgeDevices' });
  const { t: tc } = useTranslation('common');
  const { currentCompany } = useAuth();
  
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInCompanyTimezone(dateString, currentCompany?.timezone || 'UTC');
  }, [currentCompany?.timezone]);

  const getStatusBadge = useCallback((status: number) => {
    return status === 1 ? (
      <Badge bg="success">{tc('status.active')}</Badge>
    ) : (
      <Badge bg="danger">{tc('status.inactive')}</Badge>
    );
  }, [tc]);

  return (
    <Card className="mobile-edgedevice-card m-3 an-data-card">
      <Card.Body className="an-data-card">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <EdgeDeviceIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(edgeDevice.name)}</h6>
              <small className="text-muted">ID: {renderCellValue(edgeDevice.id)}</small>
            </div>
          </div>
          {getStatusBadge(edgeDevice.status)}
        </div>
        
        <Row className="g-2 mb-3">
          <Col xs={12}>
            <small className="text-muted d-block">{t('companyIdColumn')}</small>
            <span className="fw-medium">{renderCellValue(edgeDevice.company_id)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{t('apiTokenHash')}</small>
            <code className="small">{renderCellValue(edgeDevice.api_token_hash ? edgeDevice.api_token_hash.substring(0, 12) + '...' : tc('messages.na'))}</code>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{t('registeredAt')}</small>
            <span>{formatDateTime(edgeDevice.registered_at)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{t('tokenExpiration')}</small>
            <span>{formatDateTime(edgeDevice.token_expiration_date)}</span>
          </Col>
        </Row>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {edgeDevice.created_at ? formatDateTime(edgeDevice.created_at) : tc('messages.na')}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
});

EdgeDeviceCard.displayName = 'EdgeDeviceCard';

// Memoized Table Row Component
const EdgeDeviceTableRow = memo(({ 
  edgeDevice, 
  companyTimezone
}: { 
  edgeDevice: EdgeDevice; 
  companyTimezone: string;
}) => {
  const { t: tc } = useTranslation('common');
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInCompanyTimezone(dateString, companyTimezone);
  }, [companyTimezone, tc]);

  const getStatusBadge = useCallback((status: number) => {
    return status === 1 ? (
      <Badge bg="success">{tc('status.active')}</Badge>
    ) : (
      <Badge bg="danger">{tc('status.inactive')}</Badge>
    );
  }, [tc]);

  return (
    <tr>
      <td>{getStatusBadge(edgeDevice.status)}</td>
      <td>
        <div className="d-flex align-items-center">
          <EdgeDeviceIcon />
          <span className="fw-medium">{renderCellValue(edgeDevice.name)}</span>
        </div>
      </td>
      <td><code className="small">{renderCellValue(edgeDevice.id)}</code></td>
      <td><code className="small">{renderCellValue(edgeDevice.company_id)}</code></td>
      <td><code className="small">{renderCellValue(edgeDevice.api_token_hash ? edgeDevice.api_token_hash.substring(0, 12) + '...' : tc('messages.na'))}</code></td>
      <td>
        <small className="text-muted">
          {formatDateTime(edgeDevice.registered_at)}
        </small>
      </td>
      <td>
        <small className="text-muted">
          {formatDateTime(edgeDevice.token_expiration_date)}
        </small>
      </td>
      <td>
        <small className="text-muted">
          {edgeDevice.created_at ? formatDateTime(edgeDevice.created_at) : tc('messages.na')}
        </small>
      </td>
    </tr>
  );
});

EdgeDeviceTableRow.displayName = 'EdgeDeviceTableRow';

const EdgeDevices: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'edgeDevices' });
  const { t: tc } = useTranslation('common');
  const { currentCompany } = useAuth();
  
  // Get company timezone with fallback
  const companyTimezone = currentCompany?.timezone || 'UTC';
  
  const [edgeDevices, setEdgeDevices] = useState<EdgeDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<CreateEdgeDeviceRequest>(() => {
    const storedCompany = localStorage.getItem('currentCompany');
    let initialTz = 'UTC';
    let companyId = '';
    if (storedCompany) {
      try {
        const parsed = JSON.parse(storedCompany);
        initialTz = parsed.timezone || 'UTC';
        companyId = parsed.company_id || '';
      } catch (e) {}
    }
    
    const getInitialDateTime = (tz: string, daysOffset = 0) => {
      const now = new Date();
      if (daysOffset !== 0) {
        now.setDate(now.getDate() + daysOffset);
      }
      const companyDate = new Date(now.toLocaleString('en-US', { timeZone: tz }));
      const Y = companyDate.getFullYear();
      const M = String(companyDate.getMonth() + 1).padStart(2, '0');
      const D = String(companyDate.getDate()).padStart(2, '0');
      const h = String(companyDate.getHours()).padStart(2, '0');
      const m = String(companyDate.getMinutes()).padStart(2, '0');
      return `${Y}-${M}-${D}T${h}:${m}`;
    };
    
    return {
      name: '',
      company_id: companyId,
      api_token_hash: '',
      registered_at: getInitialDateTime(initialTz),
      status: 1,
      token_expiration_date: getInitialDateTime(initialTz, 7)
    };
  });

  // Check if user has edge device permissions
  const { canManageEdgeDevices, canAdd } = useRolePermissions();

  const fetchEdgeDevices = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await edgeDeviceAPI.getEdgeDevices(currentCompany.company_id);
      
      // Handle both array response and object response formats
      if (Array.isArray(response)) {
        setEdgeDevices(response);
      } else {
        setEdgeDevices(response.devices || []);
      }
    } catch (error: any) {
      console.error('Error fetching edge devices:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany, t]);

  useEffect(() => {
    fetchEdgeDevices();
  }, [fetchEdgeDevices]);

  // Update company_id in form when currentCompany changes
  useEffect(() => {
    if (currentCompany) {
      setFormData(prev => ({ ...prev, company_id: currentCompany.company_id }));
    }
  }, [currentCompany]);

  // Memoized filtered edge devices to prevent unnecessary re-computations
  const filteredEdgeDevices = useMemo(() => {
    if (!searchTerm.trim()) return edgeDevices;
    
    const searchLower = searchTerm.toLowerCase();
    return edgeDevices.filter(device => 
      device.name?.toLowerCase().includes(searchLower) ||
      device.id?.toString().includes(searchLower) ||
      device.company_id?.toLowerCase().includes(searchLower) ||
      device.api_token_hash?.toLowerCase().includes(searchLower)
    );
  }, [edgeDevices, searchTerm]);

  const totalPages = Math.ceil(filteredEdgeDevices.length / rowsPerPage);
  const paginatedEdgeDevices = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredEdgeDevices.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredEdgeDevices, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  }, []);



  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Modal handlers
  const handleShowModal = useCallback(() => {
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    
    const tz = currentCompany?.timezone || 'UTC';
    const getInitialDateTime = (t: string | Date, daysOffset = 0) => {
      const d = new Date(t);
      if (daysOffset !== 0) {
        d.setDate(d.getDate() + daysOffset);
      }
      const companyDate = new Date(d.toLocaleString('en-US', { timeZone: tz }));
      const Y = companyDate.getFullYear();
      const M = String(companyDate.getMonth() + 1).padStart(2, '0');
      const D = String(companyDate.getDate()).padStart(2, '0');
      const h = String(companyDate.getHours()).padStart(2, '0');
      const m = String(companyDate.getMinutes()).padStart(2, '0');
      return `${Y}-${M}-${D}T${h}:${m}`;
    };

    setFormData({
      name: '',
      company_id: currentCompany?.company_id || '',
      api_token_hash: '',
      registered_at: getInitialDateTime(new Date()),
      status: 1,
      token_expiration_date: getInitialDateTime(new Date(), 7)
    });
  }, [currentCompany]);

  const handleFormChange = useCallback((field: keyof CreateEdgeDeviceRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany) return;

    try {
      setModalLoading(true);
      setModalError(null);
      setModalSuccess(null);

      await edgeDeviceAPI.createEdgeDevice(formData);
      
      setModalSuccess(t('createSuccess'));
      
      // Refresh the list
      await fetchEdgeDevices();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error creating edge device:', error);
      setModalError(error.response?.data?.message || error.message || t('createFailed'));
    } finally {
      setModalLoading(false);
    }
  }, [formData, currentCompany, fetchEdgeDevices, handleCloseModal]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when company changes
  useEffect(() => {
    if (currentCompany) {
      clearCache(`edgedevices_${currentCompany.company_id}`);
      setSearchTerm('');
      setPage(1);
    }
  }, [currentCompany]);

  // If user doesn't have edge device permissions, show access denied
  if (!canManageEdgeDevices) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page">
          <Alert variant="danger">
            <Alert.Heading>{t('accessDeniedTitle')}</Alert.Heading>
            <p>
              {t('accessDeniedBody')}
            </p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        <div className="page-header">
          <h2 className="an-page-title">
            {t('title')}
          </h2>
          {currentCompany && canAdd('edge_devices') && (
            <Button variant="primary" onClick={handleShowModal}>
              <i className="bi bi-plus-lg"></i>
              {t('addDevice')}
            </Button>
          )}
        </div>
        
        {currentCompany && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
          </Alert>
        )}

        {!currentCompany && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectCompany', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search Bar */}
        {currentCompany && (
          <Card className="mb-4 an-data-card">
            <Card.Body className="an-data-card">
              <Row className="g-3">
                <Col md={8}>
                  <InputGroup>
                    <InputGroup.Text>
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    {searchTerm && (
                      <Button variant="outline-secondary" onClick={clearSearch}>
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    )}
                  </InputGroup>
                  {searchTerm && (
                    <small className="text-muted mt-2 d-block">
                      {t('found', { count: filteredEdgeDevices.length, term: searchTerm })}
                    </small>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="p-0 an-data-card">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedEdgeDevices.length > 0 ? (
                  paginatedEdgeDevices.map((edgeDevice) => (
                    <EdgeDeviceCard
                      key={edgeDevice.id}
                      edgeDevice={edgeDevice}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <EdgeDeviceIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                        : t('noneRegistered', { company: currentCompany.description })
                      }
                    </p>
                    <Button variant="primary" onClick={handleShowModal}>
                      <i className="bi bi-plus-lg"></i>
                      {t('addFirstDevice')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive an-data-table an-panel">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{tc('labels.status')}</th>
                        <th>{t('deviceName')}</th>
                        <th>ID</th>
                        <th>{t('companyIdColumn')}</th>
                        <th>{t('apiTokenHash')}</th>
                        <th>{t('registeredAt')}</th>
                        <th>{t('tokenExpiration')}</th>
                        <th>{tc('labels.created')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEdgeDevices.length > 0 ? (
                        paginatedEdgeDevices.map((edgeDevice) => (
                          <EdgeDeviceTableRow
                            key={edgeDevice.id}
                            edgeDevice={edgeDevice}
                            companyTimezone={companyTimezone}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center py-5">
                            <EdgeDeviceIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                                : t('noneRegistered', { company: currentCompany.description })
                              }
                            </p>
                            <Button variant="primary" onClick={handleShowModal}>
                              <i className="bi bi-plus-lg"></i>
                              {t('addFirstDevice')}
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {filteredEdgeDevices.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, filteredEdgeDevices.length), total: filteredEdgeDevices.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0 an-pagination">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {!currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="text-center py-5 an-data-card">
              <h5>{tc('messages.selectCompanyTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectCompanyBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create Edge Device Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('companyIdLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.company_id}
                      onChange={(e) => handleFormChange('company_id', e.target.value)}
                      placeholder={t('companyIdPlaceholder')}
                      required
                      readOnly
                    />
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('apiTokenLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.api_token_hash}
                      onChange={(e) => handleFormChange('api_token_hash', e.target.value)}
                      placeholder={t('apiTokenPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('registeredAtLabel')}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={formData.registered_at}
                      onChange={(e) => handleFormChange('registered_at', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('statusLabel')}</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => handleFormChange('status', parseInt(e.target.value))}
                      required
                    >
                      <option value={1}>{tc('status.active')}</option>
                      <option value={0}>{tc('status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('tokenExpirationLabel')}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={formData.token_expiration_date}
                      onChange={(e) => handleFormChange('token_expiration_date', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.creating')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg"></i>
                    {t('createDevice')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default EdgeDevices; 

